import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';

import { type Category } from '../../api/categories';
import { scheduleApi, type Schedule } from '../../api/schedule';
import { formatDateIST, formatTimeIST } from '../../utils/function';

import ConfirmModal from '../model/ConfirmModal';
import { CrudActions } from '../ui/action-menu';
import { Badge } from '../ui/badge';
import { CustomPagination } from '../ui/CustomPagination';
import Modal from '../model/Modal';
import { ScheduleTableSkeleton } from '../ui/skeleton';
import { cn } from '../ui/utils';

import { CategoryDisplay } from '../common/CategorySelector';
// import ScheduleFormOld from './ScheduleFormOld';
import ViewScheduleModal from './ViewScheduleModal';
import { SearchAndFilter } from '../common/SearchAndFilter';
import ScheduleForm from './ScheduleForm';
import SongForm from './SongForm';

type Status = "all" | "completed" | "cancelled" | "live" | "not_started" | "upcoming"
type Section = "schedule" | "songs";
type Mode = "add" | "edit";


export default function ScheduleManager() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [createdScheduleId, setCreatedScheduleId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<Section>("schedule");
  const [mode, setMode] = useState<Mode>("add");

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearch] = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState<Status>(
    (searchParams.get('status') as Status) || "all"
  );

  const page = Number(searchParams.get('page') ?? 1);
  const limit = Number(searchParams.get('limit') ?? 10);

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  const [viewingSchedule, setViewingSchedule] = useState<Schedule | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; id: string | null }>({
    isOpen: false,
    id: null,
  });

  const updateSearchParams = (params: {
    search?: string;
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(params).forEach(([key, value]) => {
      if (!value || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });

    setSearchParams(newParams);
  };

  const handleStatusChange = (value: Status) => {
    setStatusFilter(value);
    updateSearchParams({
        status: value,
        page: 1
    });
  };

  const fetchSchedules = async (
    page: number,
    limit: number,
    search = '',
    status = 'all'
  ) => {
    try {
      setIsLoading(true);
      const response = await scheduleApi.getSchedules({
        page,
        limit,
        search,
        status
      });

      if (response.success) {
        setSchedules(response.data);
        setPagination({
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
        });
      } else {
        setSchedules([]);
        setPagination({ total: 0, totalPages: 1 });
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch schedules');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updateSearchParams({ search: debouncedSearch, page: 1, limit, status: statusFilter });
    fetchSchedules(1, limit, debouncedSearch, statusFilter);
  }, [debouncedSearch, limit, statusFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage !== page) {
      updateSearchParams({ page: newPage, limit });
      fetchSchedules(newPage, limit, debouncedSearch, statusFilter);
    }
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    updateSearchParams({ page: 1, limit: newLimit });
    fetchSchedules(1, newLimit, debouncedSearch);
  };

  const handleDelete = async () => {
    if (!confirmModal.id) return;

    try {
      await scheduleApi.deleteSchedule(confirmModal.id);
      toast.success('Schedule deleted');
      fetchSchedules(page, limit, debouncedSearch, statusFilter);
    } catch {
      toast.error('Failed to delete schedule');
    } finally {
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  const handleAddSchedule = () => {
    setEditingSchedule(null);
    setIsFormModalOpen(true);
    setCreatedScheduleId(null);

    setMode("add");
    setActiveSection("schedule");
  };

  const handleEditSchedule = (schedule: Schedule) => {
    setMode("edit");                // 🔑 MISSING LINE
    setEditingSchedule(schedule);
    setCreatedScheduleId(schedule.id);
    setActiveSection("schedule");
    setIsFormModalOpen(true);
  };


  const handleFormSuccess = () => {
    fetchSchedules(page, limit, debouncedSearch, statusFilter);
    setIsFormModalOpen(false);
    setEditingSchedule(null);
  };

  const handleScheduleCreated = (scheduleId: string) => {
    setCreatedScheduleId(scheduleId);
    setActiveSection("songs");
    fetchSchedules(page, limit, debouncedSearch, statusFilter);
  };

  const resetForm = () => {
    setEditingSchedule(null);
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Schedules</h1>
          <p className="text-slate-400">Manage your radio programming</p>
        </div>
        <button
          onClick={handleAddSchedule}
          className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 px-5 py-2.5 rounded-xl text-white transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Schedule
        </button>
      </div>

      {/* Search */}
      {/* <div className="bg-[#0B1623] p-4 border border-white/10 rounded-2xl">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search schedules..."
            className="w-full pl-10 pr-10 py-2.5 bg-black/20 border border-white/10 rounded-xl text-white"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      </div> */}

      <SearchAndFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onClearSearch={() => setSearchTerm('')}
          statusFilter={statusFilter}
          onStatusChange={handleStatusChange}
          filterOptions={[
              { value: "all", label: "All Schedules" },
              { value: "completed", label: "Completed" },
              { value: "upcoming", label: "Upcoming" },
              { value: "cancelled", label: "Cancelled" },
              { value: "live", label: "Live" },
          ]}
          searchPlaceholder="Search shows..."
      />

      {/* Table */}
      <div className="bg-[#0B1623] border border-white/10 rounded-2xl overflow-x-auto">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="px-6 py-4 text-left text-xs text-slate-400">Show</th>
              <th className="px-6 py-4 text-left text-xs text-slate-400">Host</th>
              <th className="px-6 py-4 text-left text-xs text-slate-400">Categories</th>
              <th className="px-6 py-4 text-left text-xs text-slate-400">Time</th>
              <th className="px-6 py-4 text-left text-xs text-slate-400">Status</th>
              <th className="px-6 py-4 text-right text-xs text-slate-400">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-white/5">
            {isLoading ? (
              <ScheduleTableSkeleton />
            ) : schedules.length ? (
              schedules.map((schedule) => (
                <tr key={schedule.id} className="hover:bg-white/5">
                  <td className="px-6 py-4 cursor-pointer" onClick={() => setViewingSchedule(schedule)}>
                    <div className="font-medium text-white">{schedule.show.name}</div>
                    <div className="text-xs text-slate-400">
                      {formatDateIST(schedule.date)}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-slate-300">{schedule.show.hostName}</td>

                  <td className="px-6 py-4">
                    <CategoryDisplay categories={schedule.show.categories as Category[]} />
                  </td>

                  <td className="px-6 py-4 text-xs text-slate-300">
                    {formatTimeIST(schedule.startTime)} – {formatTimeIST(schedule.endTime)}
                  </td>

                  <td className="px-6 py-4">
                    <Badge
                      variant="outline"
                      className={cn(
                        'rounded-full text-xs',
                        schedule.status === 'live' && 'bg-red-500/20 text-red-400',
                        schedule.status === 'upcoming' && 'bg-amber-500/20 text-amber-400',
                        schedule.status === 'completed' && 'bg-green-500/20 text-green-400',
                        schedule.status === 'cancelled' && 'bg-red-500/20 text-red-400',
                        schedule.status === 'not_started' && 'bg-blue-500/20 text-blue-400'
                      )}
                    >
                      {schedule.status}
                    </Badge>
                  </td>

                  <td className="px-6 py-4 text-right">
                    <CrudActions
                      onView={() => setViewingSchedule(schedule)}
                      onEdit={() => handleEditSchedule(schedule)}
                      onDelete={() =>
                        setConfirmModal({ isOpen: true, id: schedule.id })
                      }
                    />
                    {/* {
                      schedule.status === 'completed' ? 
                      <CrudActions onView={() => 
                        setViewingSchedule(schedule)}
                        onDelete={() =>
                          setConfirmModal({ isOpen: true, id: schedule.id })
                        }
                      /> : 
                      <CrudActions
                        onView={() => setViewingSchedule(schedule)}
                        onEdit={() => handleEditSchedule(schedule)}
                        onDelete={() =>
                          setConfirmModal({ isOpen: true, id: schedule.id })
                        }
                      />
                    } */}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="py-12 text-center text-slate-400">
                  No schedules found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-white/10">
            <CustomPagination
              currentPage={page}
              totalItems={pagination.total}
              itemsPerPage={limit}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        )}
      </div>

      {/* <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          resetForm();
        }}
        title={editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}
      >
        <ScheduleForm
          initialData={editingSchedule}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setIsFormModalOpen(false);
            resetForm();
          }}
        />
      </Modal> */}

      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          resetForm();
        }}
        title={
            mode === "edit"
              ? "Edit Schedule"
              : activeSection === "songs"
              ? "Add Songs For Schedule"
              : "Add New Schedule"
          }
      >
        {mode === "edit" && createdScheduleId && (
          <div className="flex gap-2 mb-4">
            <button
              className={cn(
                "px-4 py-2 rounded-lg text-sm",
                activeSection === "schedule"
                  ? "bg-cyan-600 text-white"
                  : "bg-white/5 text-slate-300"
              )}
              onClick={() => setActiveSection("schedule")}
            >
              Schedule
            </button>

            <button
              className={cn(
                "px-4 py-2 rounded-lg text-sm",
                activeSection === "songs"
                  ? "bg-cyan-600 text-white"
                  : "bg-white/5 text-slate-300"
              )}
              onClick={() => setActiveSection("songs")}
            >
              MP3 Upload
            </button>
          </div>
        )}

        {/* Content */}
        {activeSection === "schedule" && (
          <ScheduleForm
            initialData={editingSchedule}
            onCreated={handleScheduleCreated}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsFormModalOpen(false);
              setEditingSchedule(null);
              setCreatedScheduleId(null);
            }}
          />
        )}

        {activeSection === "songs" && createdScheduleId && (
          <SongForm
            scheduleId={createdScheduleId}
            onClose={() => {
              setIsFormModalOpen(false);
              setCreatedScheduleId(null);
            }}
          />
        )}
      </Modal>

      <ViewScheduleModal
        isOpen={!!viewingSchedule}
        schedule={viewingSchedule}
        bookingStatus={viewingSchedule?.status}
        onClose={() => setViewingSchedule(null)}
        onDelete={() =>
          setConfirmModal({ isOpen: true, id: viewingSchedule?.id || null })
        }
        onEdit={() => {
          if (viewingSchedule) {
            handleEditSchedule(viewingSchedule);
            setViewingSchedule(null);
          }
        }}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, id: null })}
        onConfirm={handleDelete}
        title="Delete Schedule"
        message="Are you sure you want to delete this schedule?"
      />
    </div>
  );
}
